import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { StatusBadge } from '@/components/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Package, Warehouse } from 'lucide-react';
import { toast } from 'sonner';

export default function Tarimas() {
  const [tab, setTab] = useState('gestion');
  const [warehouses, setWarehouses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tarimas, setTarimas] = useState([]);
  const [disponible, setDisponible] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newGroup, setNewGroup] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addFolio, setAddFolio] = useState('');
  const [addCantidad, setAddCantidad] = useState('');

  const fetchAll = async () => {
    const [w, g] = await Promise.all([API.get('/catalogs/warehouses'), API.get('/catalogs/groups')]);
    setWarehouses(w.data); setGroups(g.data);
  };

  const fetchTarimas = async () => {
    const params = {};
    if (warehouseId) params.warehouse_id = warehouseId;
    const [t, d] = await Promise.all([
      API.get('/tarimas', { params }),
      warehouseId ? API.get('/tarimas/disponible', { params: { warehouse_id: warehouseId } }) : Promise.resolve({ data: [] })
    ]);
    setTarimas(t.data); setDisponible(d.data);
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchTarimas(); }, [warehouseId]);

  const handleCreate = async () => {
    if (!warehouseId || !newGroup) { toast.error('Selecciona almacén y grupo'); return; }
    setSaving(true);
    try {
      const res = await API.post('/tarimas/crear', { warehouse_id: warehouseId, group_code: newGroup });
      toast.success(`Tarima creada: ${res.data.folio_tarima}`);
      setCreateOpen(false); setNewGroup(''); fetchTarimas();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const handleAdd = async () => {
    if (!addFolio || !addCantidad) { toast.error('Completa los campos'); return; }
    setSaving(true);
    try {
      const res = await API.post('/tarimas/agregar', { folio_tarima: addFolio, cantidad: parseInt(addCantidad) });
      toast.success(`Agregado: ${res.data.cantidad_actual}/${res.data.capacidad} - ${res.data.estado}`);
      setAddOpen(false); setAddFolio(''); setAddCantidad(''); fetchTarimas();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const handleBodega = async (folio) => {
    if (!window.confirm(`¿Mover tarima ${folio} a bodega?`)) return;
    try { await API.post('/tarimas/bodega', { folio_tarima: folio }); toast.success('Tarima movida a bodega'); fetchTarimas(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const openTarimas = tarimas.filter(t => ['abierta', 'incompleta'].includes(t.estado));
  const bodegaTarimas = tarimas.filter(t => t.estado === 'en_bodega');
  const shippedTarimas = tarimas.filter(t => t.estado === 'salida_camion');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-semibold">Tarimas</h1><p className="text-sm text-muted-foreground">Entarimado de acumuladores usados</p></div>
        <div className="flex items-center gap-2">
          <Select value={warehouseId} onValueChange={setWarehouseId}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Almacén" /></SelectTrigger>
            <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.nombre}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#0F6E87] hover:bg-[#0C5D73]" disabled={!warehouseId}>
            <Plus className="h-4 w-4 mr-1" /> Nueva Tarima
          </Button>
        </div>
      </div>

      {disponible.length > 0 && (
        <Card className="bg-white border">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Usados disponibles para entarimar</CardTitle></CardHeader>
          <CardContent><div className="flex flex-wrap gap-3">
            {disponible.map(d => (<div key={d.group_code} className="px-3 py-2 rounded-lg border bg-muted/30 text-sm"><span className="font-medium">{d.group_code}</span>: {d.disponible} pzas</div>))}
          </div></CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="gestion">Incompletas ({openTarimas.length})</TabsTrigger>
          <TabsTrigger value="bodega">En Bodega ({bodegaTarimas.length})</TabsTrigger>
          <TabsTrigger value="enviadas">Enviadas ({shippedTarimas.length})</TabsTrigger>
        </TabsList>
        {[{ key: 'gestion', data: openTarimas, showActions: true },
          { key: 'bodega', data: bodegaTarimas },
          { key: 'enviadas', data: shippedTarimas }].map(({ key, data, showActions }) => (
          <TabsContent key={key} value={key} className="mt-4">
            <Card className="bg-white border"><CardContent className="p-0">
              <Table><TableHeader><TableRow className="bg-muted/30">
                <TableHead>Folio</TableHead><TableHead>Grupo</TableHead><TableHead>Progreso</TableHead>
                <TableHead className="text-right">Piezas</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Sin tarimas</TableCell></TableRow>}
                {data.map(t => (
                  <TableRow key={t.folio_tarima} className="hover:bg-muted/40">
                    <TableCell className="font-mono text-xs">{t.folio_tarima}</TableCell>
                    <TableCell className="font-medium">{t.group_code}</TableCell>
                    <TableCell className="w-40"><div className="flex items-center gap-2">
                      <Progress value={(t.cantidad_actual / t.capacidad) * 100} className="h-2 flex-1" />
                      <span className="text-xs text-muted-foreground">{Math.round((t.cantidad_actual / t.capacidad) * 100)}%</span>
                    </div></TableCell>
                    <TableCell className="text-right font-semibold">{t.cantidad_actual}/{t.capacidad}</TableCell>
                    <TableCell><StatusBadge status={t.estado} /></TableCell>
                    <TableCell className="text-right">
                      {showActions && <Button size="sm" variant="outline" onClick={() => { setAddFolio(t.folio_tarima); setAddOpen(true); }}><Package className="h-3.5 w-3.5 mr-1" /> Agregar</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody></Table>
            </CardContent></Card>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent><DialogHeader><DialogTitle>Nueva Tarima</DialogTitle></DialogHeader>
          <div><label className="text-sm font-medium">Grupo</label>
            <Select value={newGroup} onValueChange={setNewGroup}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona grupo" /></SelectTrigger>
              <SelectContent>{groups.map(g => <SelectItem key={g.codigo} value={g.codigo}>{g.codigo} (Cap: {g.capacidad_tarima})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#0F6E87] hover:bg-[#0C5D73]">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent><DialogHeader><DialogTitle>Agregar a Tarima {addFolio}</DialogTitle></DialogHeader>
          <div><label className="text-sm font-medium">Cantidad de acumuladores</label>
            <Input type="number" min="1" value={addCantidad} onChange={(e) => setAddCantidad(e.target.value)} className="mt-1" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancelar</Button>
            <Button onClick={handleAdd} disabled={saving} className="bg-[#0F6E87] hover:bg-[#0C5D73]">Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
